import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  GraduationCap,
  ArrowLeft,
  User as UserIcon,
  FileText,
  Shield,
  Database,
  AlertTriangle,
  Download,
  Trash2,
  LogOut,
  KeyRound,
  Loader2,
  CreditCard,
  Activity,
  LifeBuoy,
  Settings,
} from "lucide-react";
import { useManageSubscription } from "@/hooks/useManageSubscription";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogClose as DialogCloseBtn,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  exportMyData,
  getAccountStats,
  deleteAllGenerations,
  deleteAccount,
} from "@/lib/account.functions";
import { MyVoiceCard } from "@/components/MyVoiceCard";
import { ParentLockCard } from "@/components/ParentLockCard";

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Account Settings — SmartEssay" },
      { name: "description", content: "Manage your SmartEssay profile, security, privacy, and data." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const navigate = useNavigate();
  const fetchStats = useServerFn(getAccountStats);
  const fetchExport = useServerFn(exportMyData);
  const runDeleteAll = useServerFn(deleteAllGenerations);
  const runDeleteAccount = useServerFn(deleteAccount);
  const { manage: managePortal, loading: portalLoading } = useManageSubscription();

  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/auth" });
      else setSessionReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/auth" });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  const stats = useQuery({
    queryKey: ["account-stats"],
    queryFn: () => fetchStats(),
    enabled: sessionReady,
  });

  const [exporting, setExporting] = useState(false);
  const [deletingHistory, setDeletingHistory] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);

  // Danger zone state
  const [dangerOpen, setDangerOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canDelete = confirmText === "DELETE" && acknowledge && !deleting;

  async function handleExport() {
    setExporting(true);
    try {
      const data = await fetchExport();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smartessay-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Your data export is downloading.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setExporting(false);
    }
  }

  async function handleDeleteHistory() {
    setDeletingHistory(true);
    try {
      const res = await runDeleteAll();
      toast.success(`Removed ${res.count} item(s). Backups purged within 30 days.`);
      stats.refetch();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setDeletingHistory(false);
    }
  }

  async function handlePasswordReset() {
    const email = stats.data?.profile?.email;
    if (!email) {
      toast.error("No email on file.");
      return;
    }
    setResettingPw(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;
      toast.success("Password reset email sent.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setResettingPw(false);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/" });
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await runDeleteAccount({ data: { confirmation: "DELETE", acknowledge: true } });
      await supabase.auth.signOut();
      toast.success("Your account has been deleted. We're sorry to see you go.");
      navigate({ to: "/" });
    } catch (err) {
      toast.error((err as Error).message);
      setDeleting(false);
    }
  }

  if (!sessionReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hero-gradient">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2 font-display text-lg font-semibold">
          <GraduationCap className="h-5 w-5 text-primary" /> SmartEssay
        </Link>
        <Button asChild variant="ghost" size="sm">
          <Link to="/generator">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Dashboard
          </Link>
        </Button>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-6 pb-24">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            Account Settings
          </h1>
          <p className="mt-1 text-muted-foreground">
            Manage your profile, security, and privacy preferences.
          </p>
        </div>

        {/* Profile */}
        <Card id="profile">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-primary" /> Profile
            </CardTitle>
            <CardDescription>Your basic account information.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">Name: </span>
              <span className="font-medium">{stats.data?.profile?.full_name ?? "—"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Email: </span>
              <span className="font-medium">{stats.data?.profile?.email ?? "—"}</span>
            </div>
            {stats.data?.profile?.created_at && (
              <div>
                <span className="text-muted-foreground">Member since: </span>
                <span className="font-medium">
                  {new Date(stats.data.profile.created_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription & Billing */}
        <Card id="subscription">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" /> Subscription & Billing
            </CardTitle>
            <CardDescription>Your current plan and renewal details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {(() => {
              const sub = stats.data?.subscriptions?.[0];
              if (!sub) {
                return (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-muted-foreground">You're on the free plan.</p>
                    <Button asChild size="sm"><Link to="/pricing">See plans</Link></Button>
                  </div>
                );
              }
              const planLabel =
                sub.price_id === "pro_yearly_4900" ? "Pro — Yearly ($49/yr)"
                : sub.price_id === "pro_monthly_499" ? "Pro — Monthly ($4.99/mo)"
                : sub.price_id === "pro_monthly" ? "Pro — Monthly (grandfathered)"
                : sub.price_id ?? "Pro";
              const renewsAt = sub.current_period_end
                ? new Date(sub.current_period_end as string).toLocaleDateString()
                : null;
              const statusLabel =
                sub.status === "active" && sub.cancel_at_period_end ? "Canceling at period end"
                : sub.status === "past_due" ? "Payment failed — retrying"
                : (sub.status ?? "").charAt(0).toUpperCase() + (sub.status ?? "").slice(1);
              return (
                <>
                  <div><span className="text-muted-foreground">Plan: </span><span className="font-medium">{planLabel}</span></div>
                  <div><span className="text-muted-foreground">Status: </span><span className="font-medium">{statusLabel}</span></div>
                  {renewsAt && (
                    <div>
                      <span className="text-muted-foreground">
                        {sub.cancel_at_period_end ? "Access until: " : "Renews on: "}
                      </span>
                      <span className="font-medium">{renewsAt}</span>
                    </div>
                  )}
                  <div className="pt-1">
                    <Button size="sm" onClick={managePortal} disabled={portalLoading}>
                      <Settings className="mr-1.5 h-4 w-4" />
                      {portalLoading ? "Opening…" : "Manage subscription"}
                    </Button>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>


        {/* My Essays */}
        <Card id="essays">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" /> My Essays
            </CardTitle>
            <CardDescription>Your saved outlines and history.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <p>
              You have{" "}
              <span className="font-semibold">{stats.data?.generationCount ?? 0}</span>{" "}
              saved outline(s).
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/history">View history</Link>
            </Button>
          </CardContent>
        </Card>
        {/* My Voice */}
        <MyVoiceCard />

        {/* Recent activity */}
        <Card id="activity">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" /> Recent activity
            </CardTitle>
            <CardDescription>Your five most recent outlines.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {(() => {
              const items = stats.data?.recentGenerations ?? [];
              if (items.length === 0) {
                return <p className="text-muted-foreground">No outlines yet. <Link to="/generator" className="text-primary underline">Create your first one</Link>.</p>;
              }
              return (
                <ul className="divide-y divide-border">
                  {items.map((g) => (
                    <li key={g.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <Link
                          to="/history/$id"
                          params={{ id: g.id }}
                          className="block truncate font-medium hover:underline"
                        >
                          {g.title || "Untitled outline"}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {g.assignment_type ? `${g.assignment_type} · ` : ""}
                          {new Date(g.created_at).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </CardContent>
        </Card>

        {/* Support / contact records */}
        <Card id="support">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5 text-primary" /> Support & billing alerts
            </CardTitle>
            <CardDescription>
              Times checkout was blocked on your account. Share these with support if you need help.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            {(() => {
              const blocks = stats.data?.checkoutBlocks ?? [];
              if (blocks.length === 0) {
                return (
                  <p className="text-muted-foreground">
                    No checkout issues on record. Need help? Email{" "}
                    <a href="mailto:support@tilt.university" className="text-primary underline">
                      support@tilt.university
                    </a>.
                  </p>
                );
              }
              return (
                <>
                  <ul className="divide-y divide-border">
                    {blocks.map((b) => (
                      <li key={b.id} className="py-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-medium">{b.attempted_price_id}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(b.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {b.block_reason === "db_active_subscription"
                            ? "Existing active subscription (our records)"
                            : b.block_reason === "stripe_active_subscription"
                            ? "Existing active subscription (Stripe)"
                            : b.block_reason}
                          {b.existing_status ? ` · ${b.existing_status}` : ""}
                          {` · ${b.environment}`}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Need to resolve one of these? Email{" "}
                    <a href="mailto:support@tilt.university" className="text-primary underline">
                      support@tilt.university
                    </a>.
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>




        {/* Security */}
        <Card id="security">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" /> Security
            </CardTitle>
            <CardDescription>Manage your password and active sessions.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handlePasswordReset} disabled={resettingPw}>
              <KeyRound className="mr-1.5 h-4 w-4" />
              {resettingPw ? "Sending..." : "Send password reset email"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </CardContent>
        </Card>

        <ParentLockCard />

        {/* Privacy & Data */}
        <Card id="privacy">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" /> Privacy & Data
            </CardTitle>
            <CardDescription>
              Export or clear the data SmartEssay holds about you.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border p-4">
              <div className="max-w-md text-sm">
                <div className="font-medium">Export all my data (JSON)</div>
                <p className="text-muted-foreground">
                  Download a complete copy of your profile, outlines, and history.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
                <Download className="mr-1.5 h-4 w-4" />
                {exporting ? "Preparing..." : "Export JSON"}
              </Button>
            </div>

            <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border p-4">
              <div className="max-w-md text-sm">
                <div className="font-medium">Delete all my outlines & history</div>
                <p className="text-muted-foreground">
                  Immediately remove every saved outline. Backups will be purged within 30 days.
                </p>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Trash2 className="mr-1.5 h-4 w-4" /> Delete history
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete all outlines?</DialogTitle>
                    <DialogDescription>
                      This will permanently remove every saved outline and source text from your
                      account. Your account itself will stay active. Backups are purged within 30 days.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogCloseBtn asChild>
                      <Button variant="ghost">Cancel</Button>
                    </DialogCloseBtn>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteHistory}
                      disabled={deletingHistory}
                    >
                      {deletingHistory ? "Deleting..." : "Yes, delete everything"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card id="danger" className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Danger Zone
            </CardTitle>
            <CardDescription>
              Permanently delete your account. This action complies with your right to erasure
              under GDPR, CCPA, and FERPA.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog open={dangerOpen} onOpenChange={(o) => { setDangerOpen(o); if (!o) { setConfirmText(""); setAcknowledge(false); } }}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full sm:w-auto">
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  🗑️ Permanently Delete My Account & All Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will delete your account, all essays, prompts, and history forever
                    (except required billing records). You cannot undo this. Type{" "}
                    <span className="font-semibold text-foreground">DELETE</span> to confirm.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-delete">Type DELETE to confirm</Label>
                    <Input
                      id="confirm-delete"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="DELETE"
                      autoComplete="off"
                    />
                  </div>
                  <label
                    htmlFor="ack-erasure"
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-xs leading-relaxed text-muted-foreground"
                  >
                    <Checkbox
                      id="ack-erasure"
                      checked={acknowledge}
                      onCheckedChange={(v) => setAcknowledge(v === true)}
                      className="mt-0.5"
                    />
                    <span>
                      I understand this is permanent and complies with my right to erasure under
                      GDPR / CCPA / FERPA.
                    </span>
                  </label>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    asChild
                    disabled={!canDelete}
                  >
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={!canDelete}
                    >
                      {deleting ? "Deleting..." : "Permanently delete"}
                    </Button>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </main>

    </div>
  );
}

